import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function normalizeText(s) {
  return String(s || "").toLowerCase().trim();
}

/** Walk ancestors and return elements that scroll (overflow not visible). */
function collectScrollContainers(fromEl) {
  const out = [];
  if (!fromEl) return out;
  let node = fromEl.parentElement;
  while (node && node !== document.documentElement) {
    const cs = window.getComputedStyle(node);
    const ox = cs.overflowX;
    const oy = cs.overflowY;
    if (/(auto|scroll|overlay)/.test(ox) || /(auto|scroll|overlay)/.test(oy)) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/**
 * SearchableSelect
 *
 * Reusable combobox-style select with type-to-filter and max visible rows + scroll.
 * Designed to replace ad-hoc searchable dropdowns (e.g. Task details Vendor picker).
 *
 * Options: [{ value: string, label: string, keywords?: string }]
 */
export default function SearchableSelect({
  id,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  maxVisibleItems = 4,
  dropdownPortal = true,
  /** When true, include an empty option at the top. */
  allowEmpty = true,
  emptyLabel = "— None",
  autoFocus = false,
  title,
  style,
  className,
  onBlur,
  onKeyDown,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuPortalRef = useRef(null);
  const onBlurRef = useRef(onBlur);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuFixed, setMenuFixed] = useState(null);
  const listboxId = useId();

  const list = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const selected = useMemo(() => list.find((o) => String(o?.value ?? "") === String(value ?? "")) || null, [list, value]);

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return list;
    return list.filter((o) => {
      const hay = `${o?.label ?? ""} ${o?.keywords ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [list, search]);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const scheduleFieldCommit = useCallback(() => {
    window.setTimeout(() => onBlurRef.current?.(), 0);
  }, []);

  const closeAndClear = useCallback(({ didBlur = false } = {}) => {
    setOpen(false);
    setSearch("");
    if (didBlur) scheduleFieldCommit();
  }, [scheduleFieldCommit]);

  const commitValue = useCallback(
    (nextVal) => {
      setOpen(false);
      setSearch("");
      if (onChange) onChange(nextVal);
      scheduleFieldCommit();
    },
    [onChange, scheduleFieldCommit]
  );

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (dropdownPortal && menuPortalRef.current?.contains(e.target)) return;
      closeAndClear({ didBlur: true });
    };
    const closeOnEsc = (e) => {
      if (e.key === "Escape") {
        closeAndClear({ didBlur: true });
      }
    };
    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("keydown", closeOnEsc);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEsc);
    };
  }, [open, dropdownPortal, closeAndClear]);

  useLayoutEffect(() => {
    if (!open || !rootRef.current) {
      if (!open) setMenuFixed(null);
      return;
    }
    const itemH = 34;
    const maxItems = Math.max(1, Number(maxVisibleItems) || 4);
    const desiredHeight = maxItems * itemH + 10;

    const update = () => {
      // Anchor to the visible input, not the outer wrapper (wrapper may differ when callers pass padding/border via style).
      const anchor = inputRef.current || rootRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const preferredTop = rect.bottom + 4;
      const clampedTop = Math.min(preferredTop, window.innerHeight - desiredHeight - 4);
      const top = Math.max(4, clampedTop);
      setMenuFixed({
        left: rect.left,
        width: rect.width,
        top,
        maxHeight: `${maxItems * itemH}px`,
      });
    };

    update();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      update();
      raf2 = requestAnimationFrame(update);
    });

    if (dropdownPortal) {
      const scrollParents = collectScrollContainers(rootRef.current);
      scrollParents.forEach((n) => n.addEventListener("scroll", update, { passive: true }));
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
        scrollParents.forEach((n) => n.removeEventListener("scroll", update));
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [open, maxVisibleItems, dropdownPortal]);

  /** Outer wrapper stays layout-only; consumer `style` applies to the input so the field and portal share one box (matches TimeSelect). */
  const wrapperStyle = {
    position: "relative",
    width: "100%",
    minWidth: 0,
  };

  const baseFieldStyle = {
    width: "100%",
    minWidth: 0,
    padding: "6px 32px 6px 8px",
    fontSize: "13px",
    fontFamily: "var(--font-sans)",
    borderRadius: "4px",
    border: "1px solid #ced4da",
    boxSizing: "border-box",
    outline: "none",
    background: disabled ? "#f1f3f5" : "#fff",
    cursor: disabled ? "not-allowed" : "text",
  };

  const mergedInputStyle = {
    ...baseFieldStyle,
    ...(style || {}),
    // Callers often pass shorthand padding (e.g. form rows); keep room for the right-side ▾ control.
    paddingRight: "34px",
  };

  const menuBoxStyle = {
    border: "1px solid #ced4da",
    borderRadius: "6px",
    background: "#fff",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 11000,
  };

  const renderMenu = () => (
    <div
      id={listboxId}
      role="listbox"
      style={{ ...menuBoxStyle, maxHeight: menuFixed?.maxHeight || `${Math.max(1, Number(maxVisibleItems) || 4) * 34}px` }}
    >
      <div
        role="option"
        aria-selected={allowEmpty && !String(value || "")}
        tabIndex={0}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => commitValue("")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            commitValue("");
          }
        }}
        style={{
          width: "100%",
          minHeight: 32,
          background: allowEmpty && !String(value || "") ? "#e7f1ff" : "#fff",
          display: allowEmpty ? "flex" : "none",
          alignItems: "center",
          textAlign: "left",
          padding: "6px 10px",
          fontSize: "13px",
          lineHeight: 1.2,
          color: "#6c757d",
          cursor: "pointer",
          boxSizing: "border-box",
          userSelect: "none",
        }}
      >
        {emptyLabel}
      </div>
      {filtered.map((opt) => {
        const optVal = String(opt?.value ?? "");
        const active = optVal === String(value ?? "");
        return (
          <div
            key={`${optVal}-${opt?.label ?? ""}`}
            role="option"
            aria-selected={active}
            tabIndex={0}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => commitValue(optVal)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                commitValue(optVal);
              }
            }}
            style={{
              width: "100%",
              minHeight: 32,
              background: active ? "#e7f1ff" : "#fff",
              display: "flex",
              alignItems: "center",
              textAlign: "left",
              padding: "6px 10px",
              fontSize: "13px",
              lineHeight: 1.2,
              color: "#212529",
              cursor: "pointer",
              boxSizing: "border-box",
              userSelect: "none",
            }}
          >
            {opt?.label ?? "—"}
          </div>
        );
      })}
      {filtered.length === 0 && (
        <div style={{ padding: "8px 10px", fontSize: 13, color: "#6c757d" }}>No matches</div>
      )}
    </div>
  );

  const displayValue = open ? search : selected?.label ?? "";

  return (
    <div ref={rootRef} style={wrapperStyle} className={["searchable-select", className].filter(Boolean).join(" ")}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        className="searchable-select-input"
        value={displayValue}
        disabled={disabled}
        aria-label={title || placeholder}
        autoFocus={autoFocus}
        placeholder={open ? searchPlaceholder : placeholder}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
          setSearch("");
        }}
        onChange={(e) => {
          if (disabled) return;
          setOpen(true);
          setSearch(e.target.value);
        }}
        onKeyDown={(e) => {
          if (typeof onKeyDown === "function") onKeyDown(e);
          if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
            e.preventDefault();
            setOpen(true);
            setSearch("");
            return;
          }
          if (open && e.key === "Enter") {
            // If there is exactly one match, accept it.
            if (filtered.length === 1) {
              e.preventDefault();
              const v = String(filtered[0]?.value ?? "");
              commitValue(v);
            }
          }
        }}
        onBlur={(e) => {
          // If focus moves into the menu (non-portal), don't treat it as blur yet.
          const next = e.relatedTarget;
          if (next instanceof Node) {
            if (rootRef.current?.contains(next)) return;
            if (dropdownPortal && menuPortalRef.current?.contains(next)) return;
          }
          if (typeof onBlur === "function") onBlur(e);
        }}
        style={mergedInputStyle}
        role="combobox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-haspopup="listbox"
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        aria-label="Toggle list"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setSearch("");
          if (!open) setTimeout(() => inputRef.current?.focus?.(), 0);
        }}
        disabled={disabled}
        style={{
          position: "absolute",
          right: 4,
          top: 0,
          bottom: 0,
          width: 28,
          border: "none",
          background: "transparent",
          cursor: disabled ? "not-allowed" : "pointer",
          color: "#6c757d",
          fontSize: 12,
        }}
      >
        ▾
      </button>

      {open && !dropdownPortal && (
        <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 11000 }}>
          {renderMenu()}
        </div>
      )}

      {open && dropdownPortal && menuFixed != null && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: "fixed",
              left: menuFixed.left,
              top: menuFixed.top,
              width: menuFixed.width,
              // Ensure it stacks above modals/backdrops and sticky table headers.
              zIndex: 11000,
            }}
          >
            {renderMenu()}
          </div>,
          document.body
        )}
    </div>
  );
}

